import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 思维启发与个人成长 - 横屏「思维导图」风格
 *
 * 中心主题 + 放射状分支 + SVG连线动画 + 节点逐个展开
 * 适合：思维模型、知识体系、概念关系、成长框架
 * 尺寸：1920x1080 横屏
 */

interface MindBranch {
  label: string;
  icon: string;
  detail: string;
  color: string;
}

interface MindsetMindmapProps {
  centralTopic: string;
  centralIcon: string;
  subtitle: string;
  branches: MindBranch[];
  insight: string;
}

const DEFAULT_PROPS: MindsetMindmapProps = {
  centralTopic: '深度工作',
  centralIcon: '🧠',
  subtitle: 'Deep Work — Cal Newport',
  branches: [
    { label: '消除干扰', icon: '🔕', detail: '关闭通知、设定"免打扰"时段', color: '#ef4444' },
    { label: '时间块划分', icon: '⏰', detail: '90分钟专注块 + 20分钟休息', color: '#f59e0b' },
    { label: '刻意练习', icon: '🎯', detail: '在舒适区边缘挑战自我', color: '#10b981' },
    { label: '仪式感', icon: '☕', detail: '固定地点、固定流程启动深度状态', color: '#6366f1' },
    { label: '量化产出', icon: '📊', detail: '追踪深度工作小时数而非工作时长', color: '#ec4899' },
    { label: '恢复能力', icon: '🌿', detail: '白天不做完的事别占用晚间大脑', color: '#0891b2' },
  ],
  insight: '在注意力稀缺的时代，深度工作是最有价值的超能力',
};

const BRANCH_POSITIONS = [
  { x: -380, y: -220 },
  { x: 380, y: -220 },
  { x: -440, y: 40 },
  { x: 440, y: 40 },
  { x: -380, y: 280 },
  { x: 380, y: 280 },
];

export const MindsetMindmapTemplate: React.FC<Partial<MindsetMindmapProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const cx = 960;
  const cy = 470;

  // Center node (0-40)
  const centerScale = spring({ frame, fps, from: 0, to: 1, config: { damping: 10, stiffness: 100, mass: 0.8 } });
  const centerGlow = interpolate(Math.sin(frame / 20), [-1, 1], [0.3, 0.6]);

  // Branches
  const branchStart = 50;
  const branchInterval = 45;

  // Insight
  const insightStart = branchStart + p.branches.length * branchInterval + 30;
  const insightOpacity = interpolate(frame, [insightStart, insightStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        background: '#0c0c1a',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Background subtle grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.025) 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* SVG connections */}
      <svg
        style={{ position: 'absolute', inset: 0 }}
        width="1920"
        height="1080"
        viewBox="0 0 1920 1080"
      >
        {p.branches.map((branch, i) => {
          const pos = BRANCH_POSITIONS[i % BRANCH_POSITIONS.length];
          const start = branchStart + i * branchInterval;
          const lineProgress = interpolate(frame, [start, start + 25], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });

          const tx = cx + pos.x;
          const ty = cy + pos.y;

          // Bezier control points for curved line
          const midX = (cx + tx) / 2;
          const midY = (cy + ty) / 2 - 30;

          const pathD = `M${cx},${cy} Q${midX},${midY} ${tx},${ty}`;

          return (
            <g key={i}>
              <path
                d={pathD}
                fill="none"
                stroke={`${branch.color}40`}
                strokeWidth="2"
                strokeDasharray="600"
                strokeDashoffset={600 * (1 - lineProgress)}
                strokeLinecap="round"
              />
              {/* Glow effect on line */}
              <path
                d={pathD}
                fill="none"
                stroke={`${branch.color}15`}
                strokeWidth="8"
                strokeDasharray="600"
                strokeDashoffset={600 * (1 - lineProgress)}
                strokeLinecap="round"
              />
            </g>
          );
        })}
      </svg>

      {/* Center node */}
      <div
        style={{
          position: 'absolute',
          left: cx,
          top: cy,
          transform: `translate(-50%, -50%) scale(${centerScale})`,
        }}
      >
        {/* Glow ring */}
        <div
          style={{
            position: 'absolute',
            inset: -20,
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(99,102,241,${centerGlow * 0.2}), transparent 70%)`,
          }}
        />
        <div
          style={{
            width: 160,
            height: 160,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
            border: '3px solid rgba(99,102,241,0.4)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0 0 40px rgba(99,102,241,0.2)',
          }}
        >
          <div style={{ fontSize: 44 }}>{p.centralIcon}</div>
          <div style={{ fontSize: 25, fontWeight: 800, marginTop: 4, letterSpacing: '0.05em' }}>{p.centralTopic}</div>
        </div>
      </div>

      {/* Branch nodes */}
      {p.branches.map((branch, i) => {
        const pos = BRANCH_POSITIONS[i % BRANCH_POSITIONS.length];
        const start = branchStart + i * branchInterval;
        const nodeScale = spring({
          frame: frame - start - 10,
          fps,
          from: 0,
          to: 1,
          config: { damping: 12, stiffness: 120, mass: 0.6 },
        });
        const nodeOpacity = interpolate(frame, [start + 10, start + 25], [0, 1], { extrapolateRight: 'clamp' });

        const detailOpacity = interpolate(frame, [start + 20, start + 35], [0, 1], { extrapolateRight: 'clamp' });

        const nx = cx + pos.x;
        const ny = cy + pos.y;

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: nx,
              top: ny,
              transform: `translate(-50%, -50%) scale(${nodeScale})`,
              opacity: nodeOpacity,
              textAlign: 'center',
              width: 260,
            }}
          >
            {/* Node circle */}
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 20,
                background: `${branch.color}18`,
                border: `2px solid ${branch.color}40`,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: 32,
                margin: '0 auto 10px',
              }}
            >
              {branch.icon}
            </div>

            <div style={{ fontSize: 26, fontWeight: 700, color: branch.color }}>{branch.label}</div>

            <div
              style={{
                fontSize: 22,
                color: 'rgba(255,255,255,0.5)',
                marginTop: 6,
                lineHeight: 1.4,
                opacity: detailOpacity,
              }}
            >
              {branch.detail}
            </div>
          </div>
        );
      })}

      {/* Subtitle */}
      <div
        style={{
          position: 'absolute',
          top: 30,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: centerScale,
        }}
      >
        <span style={{ fontSize: 30, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>{p.subtitle}</span>
      </div>

      {/* Insight */}
      <div
        style={{
          position: 'absolute',
          bottom: 50,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: insightOpacity,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '14px 36px',
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 40,
            border: '1px solid rgba(255,255,255,0.08)',
            fontSize: 26,
            color: 'rgba(255,255,255,0.7)',
          }}
        >
          💡 {p.insight}
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, insightStart + 40], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #6366f1, #ec4899)',
        }}
      />
    </AbsoluteFill>
  );
};
